// The Briefing's BREADCRUMB (busy-my-web#188; #204's tab shape reverted 2026-08-26 for frame
// 40007883:371422, the universal inbox's briefing state).
//
// It is a PATH again, and it is the one Saffet described in Slack (p1787738112) — "Briefing >
// 1 research pack > <name> x":
//
//   Briefing ❯ 1 research pack ❯ ⟨ Prada sunglasses ✕⟩
//   ^ root, grey    ^ a level you can click back to    ^ where you are: the arrow chip, with its ✕
//
// #204 drew this as a row of tabs with the current place FIRST and no ✕ anywhere. The universal
// inbox frame draws a left-to-right path instead, ending in a pointed chip that carries the way
// out — so the order here is OUTSIDE-IN (root first, current last), and `current` marks the tail
// rather than the head. The ✕ is back because the chip is a thing you took ON and can take off,
// which is not what a tab is.
//
// The DEPTHS are unchanged — they are the four the box can actually be in:
//
//   • a story with a kind filter on          3 Research packs · Story
//   • a Research Pack open                   Prada sunglasses · 3 research packs · Story
//   • a pack narrowed to one kind            2 Tasks · Prada sunglasses · 3 research packs · Story
//   • a task from that pack open in the box  Visit Prada store · 2 tasks · … · Story
//
// …and a story with no depth at all is a root-only trail: just "Briefing", which the strip does not
// draw on its own — with nothing taken on there is nothing to take off, and the frame's at-rest
// state keeps the briefing's own title line instead (see BriefingBlock.vue).
//
// The kebab the frames put at the far right of that header row is the PAGE header's own ⋮ menu
// (views/StoryView.vue), not part of the crumb, so it is not built here.
//
// A plain value with no closures, like every other briefing part — what a click MEANS is resolved
// in SmartBox.vue, next to the router and the store.
//
// ── …AND IT IS A JOURNEY NOW, NOT ONLY A DEPTH (busy-my-web#606, 2026-10-09; Saffet, Slack
// p1791529928953359; Pouria: "fix all things Saffet reported") ──────────────────────────────────
//   "The breadcrumbs should not disappear when I click '1 research pack'. The Breadcrumbs is there
//    to chart your journey and allow you to toggle from anywhere within the journey to take you to
//    that state. As you toggle from one state to another the purple box highlighted will show the
//    part of the Breadcrumb you have selected. When something is open like a research pack it will
//    write the name of the research pack and ', Open'."
// The rule above — the trail is WHERE THE BOX IS, the chip is always the tail, and climbing to an
// earlier crumb drops every crumb under it — is kept as the LIVE trail `briefingTrail` builds, and
// is no longer the whole picture. The page also remembers the deepest path walked on it (the
// briefing store's `journey`, fed by SmartBox), and `mergeJourney` draws THAT path whenever the live
// trail is a prefix of it: the chip (`current`) is the crumb matching where the box is, which may be
// in the middle, and the crumbs deeper than it come back marked `ahead` — grey, and clickable to
// restore that state. Walking somewhere new replaces the journey; climbing back via a crumb does not
// forget it; the ✕ on an OPEN thing does (Saffet, 2026-10-09: "there should not be a x in front of
// each breadcrumb" — the ✕ is only ever on the open thing's chip, `trailChipClose`). An open
// Research Pack's crumb reads "<name>, Open" (his words, confirmed in the same thread).

import { ATTACHMENTS_ICON, attachmentClassIcon, attachmentClassLabel } from '@/lib/attachmentsBriefing'
import { isCommerceEntry } from '@/lib/commerce'
// The possible-matches depth's own words and glyph (busy-my-web#317) — the row's, so the crumb
// cannot drift from the row it replaces.
import { MATCHES_ICON, matchesLabel } from '@/lib/briefings/email'
import { countLabel } from './rows'
// Which active rows are depths at all — a pack's view tabs are not (Saffet 2026-10-21).
import { rowTakesStrip } from './pack'
// The open email's own card (busy-my-web#620) — its kind, so the crumb can be drawn over it.
import { THREAD_KIND } from './emailThread'

// The crumb says what the row said — "3 Research packs" (busy-my-web#609, Saffet 2026-10-09).
const plural = (n, word) => countLabel(n, word)

/**
 * WHO OPENED THE THING IN THE BOX — and therefore whether there is a trail at all
 * (busy-my-web#241, Fatemeh in Slack: "breadcrumb برای زمانی هست که ما از داخل بریفینگ داریم جا به
 * جا میشیم" — the breadcrumb is for when we are moving around INSIDE the briefing).
 *
 * A breadcrumb is a record of a path WALKED. Clicking an event on the global timeline, or a step on
 * the story rail, is not a step deeper into the briefing — it is an arrival from somewhere else, and
 * the crumb it drew ("Briefing ❯ ⟨Pitchdeck ✕⟩") offered a way back to a place the user had never
 * been. Only the Briefing's own Open declares this origin, so every other entry point is silent by
 * default rather than by having to remember to say so.
 */
export const TRAIL_ORIGIN_BRIEFING = 'briefing'

/**
 * …and the ONE exception #241 now carries (busy-my-web#383).
 *
 * Saffet, opening a task from the Global timeline: "I should see the task opened in the timeline as
 * it is now but also see the story in the middle. Also possibly the breadcrumbs so you can go back
 * on your actions" — and, asked what they should read: "*Healthy Herbal Teas › Research mint tea* at
 * the top of the Task Briefing."
 *
 * #241 removed the crumb from this path because the crumb it drew ("Briefing ❯ ⟨Pitchdeck ✕⟩")
 * offered a way back to a place the user had never been. That reasoning is untouched — and this
 * journey no longer matches it, because since #383 the arrival BRINGS its own path with it: the
 * middle is navigated to the task's own ongoing, so "Healthy Herbal Teas" is somewhere the user
 * genuinely now is and can genuinely go back to. The crumb is that walk, root-first, and it is only
 * ever two segments deep: the ongoing, then the thing open on it.
 *
 * Everything else #241 named stays silent — an event opened from the story rail, a row previewed,
 * a task opened from anywhere that does not name this origin.
 */
export const TRAIL_ORIGIN_TIMELINE = 'timeline'

/** Did this open start inside the Briefing? Anything unsaid (a rail, a row, a preview) did not. */
export function openedFromBriefing(origin) {
  return String(origin || '') === TRAIL_ORIGIN_BRIEFING
}

/** …or on the Global timeline, the one arrival that brings its own story with it (#383). */
export function openedFromTimeline(origin) {
  return String(origin || '') === TRAIL_ORIGIN_TIMELINE
}

/** The first row that is a filter AND is on — the depth the strip is currently narrowed to. */
const activeFilterRow = (briefing) => (briefing?.rows || []).find(r => r && r.filter && r.active) || null

/** The frames' two glyphs, from the repo's own icon set: vuesax/outline story and 3square. */
export const TRAIL_ICON = { story: 'story', pack: 'research-pack', emails: 'sms', thread: 'sms' }

/**
 * The picker's crumb — the frame's own words. It read "Ongoing workspace" (40010912:368184) until
 * the listing became the matches table: the updated frames ("search ongoings 1–6", busy-my-web#535)
 * name the crumb after the middle's heading, "Possible ongoing matches".
 */
export const PICKER_CRUMB = 'Possible ongoing matches'

/**
 * The briefings that HAVE a depth, and therefore a trail.
 *
 * The email inbox joined them in busy-my-web#273: it used to be one flat subject (a list of sender
 * groups), and is now a dashboard you select an entry of and then a thread from — "Briefing ❯ 18
 * emails today ❯ ⟨Project kickoff agenda ✕⟩" — which is the same walk the story briefing takes and
 * therefore earns the same crumb.
 */
const KINDS_WITH_DEPTH = ['story', 'pack', 'emails']

/**
 * Is this briefing one the crumb is drawn over?
 *
 * The two that HAVE a depth — a story and a pack — plus one more since busy-my-web#277: any
 * briefing whose attachments row is open. The Attachments entry is a path the user walks INSIDE the
 * briefing ("Briefing ❯ 12 attachments ❯ PDFs"), which is Fatemeh's whole rule for when a crumb
 * exists, and it hangs off the email briefing as readily as off a story's. Nothing else about the
 * email briefing changes: with the attachments row shut it is one flat subject again and draws no
 * crumb, exactly as before.
 */
function hasDepth(briefing) {
  if (!briefing) return false
  if (briefing.kind === 'story' || briefing.kind === 'pack') return true
  return activeFilterRow(briefing)?.filter === 'attachments'
}

/**
 * Build the trail for the briefing on screen.
 *
 * @param briefing    the active briefing (lib/briefings/rows.js's shape), or null
 * @param pack        the open Research Pack (?pack=…), or null
 * @param packCount   how many packs the ongoing has — storyPacks(story).length, the one answer to
 *                    "how many packs" (lib/storySteps.js). Names the level an open pack sits under.
 * @param entityTitle the task/note/event open in the box, or '' — the deepest step of all
 * @param entityIcon  that entity's own module glyph, or '' to fall back to the kind above it
 * @param entityOrigin where that entity was opened FROM — `TRAIL_ORIGIN_BRIEFING` for the
 *                    Briefing's own Open, `TRAIL_ORIGIN_TIMELINE` for the Global timeline's (which
 *                    brings its story with it, busy-my-web#383), anything else (the story rail, a
 *                    preview) for an arrival from outside it (busy-my-web#241)
 * @param storyTitle  the ongoing the middle is showing, for the timeline journey's own two-segment
 *                    crumb ("Healthy Herbal Teas ❯ Research mint tea")
 * @param commerceTab  which tab an open commerce entry is on (busy-my-web#292): 'emails' for the
 *                    classification thread list under it, anything else for the list itself, which
 *                    is the entry and needs no level of its own
 * @param groupTitle  the Email Group opened out of the "N email groups" entry, or '' — one level
 *                    below that entry and above a thread read inside it (busy-main-bff#24)
 * @param attachmentClass the class chip narrowing the Attachments list, or '' (busy-my-web#277) —
 *                    one level BELOW the attachments row, because it is reached through it
 * @param attachmentName the file OPEN IN THE VIEWER, or '' (busy-my-web#481, 2026-09-15 parity
 *                    audit D-4) — the deepest step of the email path, under the attachments entry
 *                    and its class chip: "Briefing ❯ 24 attachments ❯ ⟨Contactpage.jpeg ✕⟩"
 * @returns [{ key, text, icon, level, filter?, current }] — CURRENT FIRST, then the levels it sits
 *          inside. `level` is what a click goes back TO; `current` marks the tab you are on.
 */
// The words a crumb falls back to when neither the card nor the journey has any for it (a page
// loaded straight onto a group, say): the level's own name rather than an empty crumb.
const FALLBACK_WORDS = { matches: 'Possible Ongoing matches', entry: 'Emails' }

export function briefingTrail({ journey = null, ...input } = {}) {
  const live = liveTrail(input)
  // The OPEN EMAIL's crumb (busy-my-web#620) is never at rest — the thread is always the chip — so
  // the #606 rename below never reaches it; a thread walked to keeps the path it was walked by.
  if (input.briefing?.kind === THREAD_KIND) return threadInJourney(live, journey)
  const merged = withoutGoneLevels(mergeJourney(live, journey), input.briefing)
    .map(s => (s.text ? s : { ...s, text: FALLBACK_WORDS[s.level] || s.level }))
  // A page at REST after a journey (busy-my-web#606, item 5): the full path stays, and the root —
  // the chip, since the box stands on it — is named the way the at-rest title line names it,
  // "Briefing · <story>", rather than the bare word the deeper crumbs use for it.
  // The email Briefing's root already carries that name at every depth (#619, `rootTextOf`), so
  // this renames it to the same words, never to the card's level title.
  const restTitle = emailRootTitleOf(input.briefing) || briefingTitleOf(input.briefing)
  if (merged.length > 1 && merged[0].current && merged[0].level === 'root' && restTitle) {
    return [{ ...merged[0], text: restTitle }, ...merged.slice(1)]
  }
  return merged
}

const briefingTitleOf = (briefing) => String(briefing?.title || '').trim()

/**
 * The EMAIL Briefing's root crumb words (busy-my-web#619, Saffet 2026-10-09): its own title,
 * "Briefing Email Inbox", as ONE crumb — "Home › Briefing Email Inbox › ⟨All emails · 962⟩". Read
 * from the card's `rootTitle` (lib/briefings/email.js's `withEmailRootTitle`), not its `title`,
 * which moves with the level ("Briefing · All Emails" at an entry). '' for every other briefing.
 */
const emailRootTitleOf = (briefing) => (briefing?.kind === 'emails' ? String(briefing.rootTitle || '').trim() : '')

/** The root crumb's words: the email Briefing's own title (#619), else the frames' bare "Briefing". */
export function rootTextOf(briefing) {
  return emailRootTitleOf(briefing) || 'Briefing'
}

/** The open Research Pack's crumb text — Saffet, 2026-10-09: "the name of the research pack and , Open". */
export const OPEN_SUFFIX = ', Open'

/**
 * THE OPEN EMAIL'S CRUMB (busy-my-web#620, Saffet 2026-10-09: "purple = where I am now").
 *
 * An email opened on its own — from Home's follow-ups, the inbox listing, a notification — is a
 * Briefing of its own, the `email-thread` card (lib/briefings/emailThread.js). Its header was one
 * plain line, "Home › Briefing . One workspace name starting October 15"; Saffet wants it to read
 * as the path it is:
 *
 *   Home ❯ Briefing ❯ ⟨One workspace name starting… ✕⟩
 *          ^ grey root   ^ the subject: where you are, the lavender chip
 *
 * The thread segment is the email path's own (`level: 'thread'`, `kind: 'emails'`), so the chip's ✕
 * is the thread's (#606 — a thread is an OPEN thing, `trailChipClose`) and steps back to the root
 * like every thread at the end of an email selection (`trailCloseTarget`). [] for a card with no
 * subject, which keeps the title line.
 */
export function openEmailTrail(card) {
  const subject = String(card?.subject || '').trim()
  if (!subject) return []
  const id = String(card?.thread?.id || '')
  return [
    { key: 'root', text: 'Briefing', icon: TRAIL_ICON.emails, level: 'root', kind: 'emails', current: false },
    { key: 'thread', text: subject, icon: TRAIL_ICON.thread, level: 'thread', kind: 'emails', ...(id ? { ref: id, restore: { id, subject } } : {}), current: true },
  ]
}

/**
 * …and the same thread REACHED BY A WALK (the Briefing's All emails, a group): the email card takes
 * over the strip the moment the reader mounts, but the journey that led here is still on the page
 * (`briefing/journey`), so when it ends in THIS thread the crumb is that path, the thread the chip —
 * "Home ❯ Briefing Email Inbox ❯ All emails · 962 ❯ ⟨Subject ✕⟩" — rather than a shorter one that
 * would also replace the journey. Anything else is the bare "Briefing ❯ ⟨Subject⟩".
 */
export function threadInJourney(live, journey) {
  const segs = (live || []).filter(Boolean)
  const tail = segs[segs.length - 1]
  const prev = (journey || []).filter(Boolean)
  const at = tail ? prev.findIndex(s => s.level === 'thread' && journeySegId(s) === journeySegId(tail)) : -1
  if (at < 1) return segs
  return [...prev.slice(0, at).map(s => ({ ...s, current: false })), tail]
}

/** Where the box IS — the trail as built before #606, root first, `current` on the tail. */
export function liveTrail({ briefing, matchesOrigin = false, pack = null, packCount = 0, entityTitle = '', entityIcon = '', entityOrigin = '', storyTitle = '', thread = '', threadRef = null, groupTitle = '', groupRef = null, categoryTitle = '', categoryKey = '', senderGroupTitle = '', senderGroupRef = '', emailEntry = '', pickerOpen = false, pickerQuery = '', attachmentClass = '', attachmentName = '', commerceTab = '' } = {}) {
  // Only the briefings that HAVE a depth. A search, an opened image and a comparison are each one
  // flat subject — there is nothing above them to go back to, and the frames draw no breadcrumb
  // over any of them.
  // …except the open email (busy-my-web#620): "Briefing ❯ ⟨<subject>⟩", see `openEmailTrail`.
  if (briefing?.kind === THREAD_KIND) return openEmailTrail(briefing)
  if (!briefing || !(KINDS_WITH_DEPTH.includes(briefing.kind) || hasDepth(briefing))) return []

  // ── The email path (busy-my-web#273) ───────────────────────────────────────────────────────
  // Two levels, and they are the two the dashboard can be walked into: an ENTRY (the row that was
  // clicked, named with its count — "18 emails today", which is what that place is called) and one
  // THREAD out of it. Built here rather than falling through the story rules below because the
  // levels underneath a story — its packs, its kinds, an entity opened in the box — say nothing
  // about a mailbox, and borrowing them would put a research pack in an email's crumb.
  if (briefing.kind === 'emails') {
    const entry = activeFilterRow(briefing)
    // The root is the email Briefing's own title, one crumb (busy-my-web#619): "Home › Briefing
    // Email Inbox › ⟨All emails · 962⟩". Every other journey's root stays "Briefing".
    const segs = [{ key: 'root', text: rootTextOf(briefing), icon: TRAIL_ICON.emails, level: 'root', kind: 'emails' }]
    // …and the possible-matches workspace (busy-my-web#317): "Briefing ❯ ⟨4 possible Ongoing
    // matches ✕⟩". It is a depth of its own and the ONLY one while it is open — the cards replace
    // the middle, so there is no entry and no thread beneath them — and the chip's ✕ is what returns
    // the box to the plain briefing. The count is the briefing's own (lib/briefings/email.js), so
    // the crumb and the row that opened it always say the same number.
    if (briefing.matches?.open) {
      segs.push({ key: 'matches', text: matchesLabel(briefing.matches.count, !!briefing.matches.graph), icon: MATCHES_ICON, level: 'matches', kind: 'emails' })
      return segs.map((s, i) => ({ ...s, current: i === segs.length - 1 }))
    }
    // …and a thread or a sender group opened by "View emails" on a MATCH (busy-my-web#608, Saffet
    // 2026-10-09) sits under the matches list it came out of — "Briefing ❯ 5 possible Ongoing
    // matches ❯ ⟨Luxury Treehouses mood ✕⟩" — so Back and the crumb both lead back to that list.
    // The count may not be on the card any more (an open group re-briefs it); the journey that
    // walked through the list supplies the words then.
    if (matchesOrigin) {
      const count = briefing.matches?.count
      segs.push({ key: 'matches', text: count ? matchesLabel(count, !!briefing.matches?.graph) : '', icon: MATCHES_ICON, level: 'matches', kind: 'emails' })
    }
    if (entry) segs.push({ key: 'entry', text: String(entry.full || entry.text), icon: entry.icon || TRAIL_ICON.emails, level: 'entry', filter: entry.filter, kind: 'emails' })
    // …and the entry the SESSION still has chosen while a sender group is open over it (Saffet,
    // 2026-10-09: "I opened the email group from the email Briefing and selected a group … the
    // breadcrumbs don't show the journey, the purple highlighted selection disappears"). An open
    // group re-briefs the card as "Briefing · LinkedIn" with no filter rows, so the entry row the
    // crumb was read from is gone; the entry is still chosen (`briefing/emailCategory`), so it is
    // still a crumb. Its words come from the journey that walked through it (`mergeJourney`).
    else if (emailEntry && String(senderGroupTitle || '').trim()) {
      segs.push({ key: 'entry', text: '', icon: TRAIL_ICON.emails, level: 'entry', filter: String(emailEntry), kind: 'emails' })
    }
    // …and, under the ATTACHMENTS entry, the class chip narrowing it (busy-my-web#258): "Briefing ❯
    // 12 attachments ❯ PDFs". The story path below has drawn this level since #277 and the mailbox's
    // card carries the SAME row (lib/briefings/email.js) over the SAME chips (AttachmentsPanel.vue),
    // but the email path returns before ever reaching that rule — so on home a user who narrowed to
    // PDFs stood in a place the crumb did not name, with its only clickable step out being the root,
    // which closes attachments altogether. Saffet, busy-my-web#258: the breadcrumb "cannot go back".
    const entryClass = entry?.filter === 'attachments' ? attachmentClassLabel(attachmentClass) : ''
    if (entryClass) {
      segs.push({
        key: 'attachment-class',
        text: entryClass,
        icon: attachmentClassIcon(attachmentClass),
        level: 'attachment-class',
        attachmentClass: String(attachmentClass),
        kind: 'emails',
      })
    }
    // …and, under the attachments entry (with or without a class chip on it), the FILE being
    // VIEWED (busy-my-web#481, 2026-09-15 parity audit D-4; frame 40011255:261043 writes the crumb
    // "… ❯ 24 Attachments ❯ ⟨Contactpage.jpeg ✕⟩").
    //
    // It RETURNS here, the way the possible-matches depth above does, and for the same reason: the
    // viewer replaces the middle. Whatever was open underneath — a thread read out of the listing,
    // a group — is still there and is found untouched when the chip's ✕ closes the file, but while
    // the file is on screen it is the only place the user is standing, and naming a conversation
    // they cannot see below it would be the crumb describing a different middle from the one in
    // front of them.
    const file = String(attachmentName || '').trim()
    if (file) {
      segs.push({
        key: 'attachment',
        text: file,
        icon: ATTACHMENTS_ICON,
        level: 'attachment',
        kind: 'emails',
      })
      return segs.map((s, i) => ({ ...s, current: i === segs.length - 1 }))
    }
    // …and, inside a COMMERCE entry, the second tab (busy-my-web#292): "Briefing ❯ 27 orders ❯
    // emails". Its own level rather than part of the entry's, because the two tabs are two places —
    // the orders, and the mail those orders were read from — and the way back to the LIST has to be
    // clickable from a thread opened out of the second one.
    if (entry && isCommerceEntry(entry.filter) && String(commerceTab) === 'emails') {
      segs.push({ key: 'commerce-emails', text: 'emails', icon: TRAIL_ICON.emails, level: 'commerce-emails', kind: 'emails' })
    }
    // …and, under the "N email groups" entry alone, the GROUP that was opened out of it
    // (busy-main-bff#24): "Briefing ❯ 14 email groups ❯ Insurance renewal". It is a third level for
    // the same reason the pack has one — the entry lists groups, and a group lists threads, so the
    // walk really is one step longer there than under the entries that list threads themselves.
    // …and, between that entry and the group, the CATEGORY the group was opened out of (frame
    // 40008255:426104, Pouria 2026-09-16: "Home ❯ Briefing ❯ 14 Email groups ❯ ⟨3 Social media
    // groups ✕⟩"). The sub-listing is a place of its own — the middle lists that category's groups
    // and the rail is retitled after it — so it is a crumb of its own, and the chip's ✕ (which
    // targets the entry above) is what closes the listing.
    //
    // …and BOTH of them belong to the groups entry ALONE (busy-my-web#528, Saffet: "the briefing
    // breadcrumbs is wrong also"). That was the rule both comments above state and neither line
    // enforced: the category and the group were pushed under WHATEVER entry was open, so opening
    // the attachments entry after having been inside a group drew
    //
    //   Home ❯ Briefing ❯ 24 attachments ❯ 3 Social media groups ❯ ⟨Insurance renewal ✕⟩
    //
    // — a path the user never walked, naming two places that are not under the entry above them and
    // are not on screen at all. (`emailSession/openEntryGroup` and `openCategory` outlive the entry
    // that opened them, which is right — closing the attachments entry finds the group again — and
    // is exactly why the crumb has to ask WHICH entry it is describing.) Corrected, the same moment
    // reads "Home ❯ Briefing ❯ 24 attachments".
    //
    // With NO entry open the two still stand: the entry row can be closed while a group is open,
    // and dropping the crumb then would lose the only way back to it (the rule this level was
    // written with). It is only a DIFFERENT entry that they do not belong under.
    const inGroupsEntry = !entry || entry.filter === 'groups'
    const category = inGroupsEntry ? String(categoryTitle || '').trim() : ''
    if (category) segs.push({ key: 'category', text: category, icon: TRAIL_ICON.emails, level: 'category', kind: 'emails', ...(categoryKey ? { ref: String(categoryKey) } : {}) })
    const group = inGroupsEntry ? String(groupTitle || '').trim() : ''
    if (group) segs.push({ key: 'group', text: group, icon: TRAIL_ICON.emails, level: 'group', kind: 'emails', ...(groupRef?.id ? { ref: String(groupRef.id), restore: groupRef } : {}) })
    // …and the SENDER group opened out of the groups listing (busy-my-web#606 / #604, Saffet
    // 2026-10-09), a level of its own: "Home ❯ Briefing ❯ 14 email groups ❯ ⟨LinkedIn⟩". Before,
    // it had no crumb at all and the trail fell back to the bare title line.
    const sender = inGroupsEntry ? String(senderGroupTitle || '').trim() : ''
    if (sender) segs.push({ key: 'sender-group', text: sender, icon: TRAIL_ICON.emails, level: 'sender-group', kind: 'emails', ref: String(senderGroupRef || sender) })
    const subject = String(thread || '').trim()
    // A thread with no entry above it is still a step INTO the briefing (a thread can be opened
    // from a list that was then closed), so it is drawn under the root rather than dropped.
    if (subject) segs.push({ key: 'thread', text: subject, icon: TRAIL_ICON.thread, level: 'thread', kind: 'emails', ...(threadRef?.id ? { ref: String(threadRef.id), restore: { id: threadRef.id, subject: threadRef.subject || subject } } : {}) })
    // …and the ONGOING WORKSPACE — the page-level picker the "Add to ongoing" search opens
    // (frame 40010912:368184, Pouria 2026-09-16): "… ❯ 5 Threads ❯ ⟨Ongoing workspace ✕⟩". The
    // deepest step of all, whatever was open under it, because the picker takes the middle; the
    // chip's ✕ steps back to whatever that was.
    if (pickerOpen) segs.push({ key: 'picker', text: PICKER_CRUMB, icon: TRAIL_ICON.story, level: 'picker', kind: 'emails' })
    // …and a SEARCH run on it, one step deeper (busy-my-web#535, search 4): "… ❯ Possible ongoing…
    // ❯ ⟨Job opportunitie… ✕⟩" — the query is the crumb, and its ✕ is back to the matches table.
    const searched = pickerOpen ? String(pickerQuery || '').trim() : ''
    if (searched) segs.push({ key: 'picker-search', text: searched, icon: TRAIL_ICON.story, level: 'picker-search', kind: 'emails' })
    return segs.map((s, i) => ({ ...s, current: i === segs.length - 1 }))
  }

  // An entity opened from OUTSIDE the briefing takes the whole strip with it (#241). Not just its
  // own segment: with an event from the timeline filling the box, the levels underneath describe
  // where the BRIEFING stands, which is not where the user is — so a crumb over it would offer a
  // way "back" out of a path nobody walked. The trail comes back untouched the moment that entity
  // is closed, because nothing about the briefing itself changed while it was up.
  const name = String(entityTitle || '').trim()
  // …EXCEPT the Global timeline's own journey (busy-my-web#383), which arrives WITH its story: the
  // middle has been navigated to the ongoing the task belongs to, so the two places the user is
  // standing in are the ongoing and the task, in that order, and both are real. Saffet's wording:
  // "Healthy Herbal Teas › Research mint tea". No ongoing (a standalone task) means no walk, and
  // #241's silence stands. Clicking the ongoing needs no rule of its own — a segment that is not
  // one of the named levels already means "back to the briefing's root", which is the story.
  if (name && openedFromTimeline(entityOrigin)) {
    const story = String(storyTitle || '').trim()
    if (!story) return []
    return [
      { key: 'story', text: story, icon: TRAIL_ICON.story, level: 'story', current: false },
      { key: 'entity', text: name, icon: entityIcon || TRAIL_ICON.story, level: 'entity', current: true },
    ]
  }
  if (name && !openedFromBriefing(entityOrigin)) return []

  // Built OUTSIDE-IN (the levels in the order they nest), then reversed once at the end: the depth
  // rules read the way the box is actually structured, and the frames' order is a presentation of
  // them rather than a second set of rules.
  // "Briefing" — the word the frame's root crumb writes, and the word Saffet's chain starts with.
  const segs = [{ key: 'root', text: 'Briefing', icon: TRAIL_ICON.story, level: 'root' }]

  if (briefing.kind === 'pack') {
    // An open pack is always reached THROUGH its ongoing's packs, so that level is named even
    // though the pack briefing has no row for it — the pack briefing describes the pack, not the
    // story it came from.
    if (packCount) segs.push({ key: 'packs', text: plural(packCount, 'research pack'), icon: TRAIL_ICON.pack, level: 'packs' })
    // …and it is an OPEN thing, so its crumb says so (busy-my-web#606, Saffet 2026-10-09: "the name
    // of the research pack and , Open"). `name` keeps the bare title for the strip's truncation
    // (the name is what gets the ellipsis; ", Open" stays readable) and for the crumb once the
    // pack is closed and only remembered.
    const packName = String(pack?.title || 'Research pack')
    segs.push({
      key: 'pack',
      text: `${packName}${OPEN_SUFFIX}`,
      name: packName,
      open: true,
      icon: TRAIL_ICON.pack,
      level: 'pack',
      ...(pack?.id ? { ref: String(pack.id), restore: { id: pack.id, ...(pack.ongoingId ? { ongoingId: pack.ongoingId } : {}) } } : {}),
    })
  }

  // The kind narrowing whatever is above it, named with its COUNT — the row drops the count when
  // it is active (its items are listed right under it) but the trail is not a list header, it is a
  // place, and "2 tasks" is what that place is called. `full` is the row's own uncounted-down text,
  // carried through briefing() for exactly this — and the row's own glyph comes with it, so the tab
  // wears the same icon as the row it stands for.
  // …but NOT a pack's view rows (Saffet, 2026-10-21: "when I click 18 extractions the Breadcrumbs
  // write it also but it is not meant to be there"). Extractions, Photos, links… switch the pack's
  // middle to a tab; they are not a place inside the pack, so the pack's own "<name>, Open" chip
  // stays where you are (lib/briefings/pack.js `rowTakesStrip`). The story kinds keep #188's crumb.
  const active = activeFilterRow(briefing)
  const row = active && rowTakesStrip(briefing.kind, active) ? active : null
  if (row) segs.push({ key: 'kind', text: String(row.full || row.text), icon: row.icon || TRAIL_ICON.story, level: 'kind', filter: row.filter })

  // …and, under an open Attachments row, the class chip narrowing it: "Briefing ❯ 12 attachments ❯
  // PDFs" (busy-my-web#277). Its own level, not a second `kind`, because clicking back to it means
  // "all the attachments again" while the kind above it means "out of attachments altogether" — two
  // different ways out, and a crumb that offers only one of them strands the user in the narrower.
  const classLabel = row?.filter === 'attachments' ? attachmentClassLabel(attachmentClass) : ''
  if (classLabel) {
    segs.push({
      key: 'attachment-class',
      text: classLabel,
      icon: attachmentClassIcon(attachmentClass),
      level: 'attachment-class',
      attachmentClass: String(attachmentClass),
    })
  }

  // …and the story's POSSIBLE CONNECTIONS table (Pouria, 2026-10-01, frame 40011046:161537): the
  // row that opens it takes the middle, so it is a depth — "Home › Briefing · Buy su… › ⟨5 Possible
  // conne… ✕⟩" — and the chip's ✕ is how the table closes (SmartBox's `onBriefingTrail`). That
  // frame heads the path with the briefing's own title ("Briefing · Buy sunglasses") rather than
  // the bare word, so the root takes it at this depth; every other story depth keeps "Briefing",
  // as its own frames write it. Not while an entity is open over it: the box is then about that.
  const relations = !name ? (briefing.rows || []).find(r => r && r.storyRelations && r.active) : null
  if (relations) {
    if (!row && briefing.title) segs[0] = { ...segs[0], text: String(briefing.title) }
    segs.push({ key: 'relations', text: String(relations.text), icon: relations.icon || TRAIL_ICON.story, level: 'relations' })
  }

  if (name) {
    segs.push({
      key: 'entity',
      text: name,
      // The entity's own module glyph where the box knows it; otherwise the kind tab above it is
      // already the right picture of what this is.
      icon: entityIcon || row?.icon || TRAIL_ICON.story,
      level: 'entity',
    })
  }

  // Root first, the way it is read; the LAST segment is where you are, and it is the one drawn as
  // the chip with the ✕.
  return segs.map((s, i) => ({ ...s, current: i === segs.length - 1 }))
}

/**
 * Is the box still at the briefing's START — nothing clicked into yet?
 *
 * A root-only trail (just the word "Briefing") is the at-rest state: nothing has been taken on, so
 * there is nothing to take off and no path to draw. The strip heads itself with the briefing's own
 * title line there — the STORY GLYPH and the story's name — and swaps that whole line for the crumb
 * the moment there is a depth to state.
 *
 * That swap is the answer to Saffet's rule (busy-my-web#225, refining #219): "You should only see
 * the Story icon when you are in the briefing at the start. As soon as you click something then it
 * goes you just see the breadcrumbs." One click into the Briefing — a filter, a pack, an item —
 * always adds a level (see the depths at the top of this file), so the icon is exactly as visible as
 * the start state is, and stepping back out to the root brings it back with it.
 *
 * It is a function here rather than a `length > 1` in the template because it is a RULE about what
 * the strip's header is, not a rendering detail: one place says when the box is at the start, and
 * the icon, the title and the crumb all follow it.
 */
export function briefingAtStart(trail) {
  return (trail || []).length <= 1
}

/** …and the crumb is drawn exactly when it is not — the two halves of one swap. */
export function briefingTrailShown(trail) {
  return !briefingAtStart(trail)
}

/**
 * Is the box standing ON an email Briefing entry — "Home › Briefing › ⟨All emails · 1,051 ✕⟩" —
 * rather than above it or inside something under it (a thread, a sender group, a class chip)?
 * The level whose prompt is "Ask Busy anything about these emails…" (busy-my-web#615).
 */
export function trailAtEmailEntry(trail) {
  const here = (trail || []).find(s => s.current)
  return !!here && here.kind === 'emails' && here.level === 'entry'
}

/**
 * WHERE THE ✕ GOES — the segment closing the current chip steps back to, or null when there is
 * nothing above it (a root-only trail carries no ✕ at all).
 *
 * For a story or a pack that is the level immediately above: the ✕ and a click on the parent crumb
 * are the same gesture, which is the rule busy-my-web#188 wrote and nothing here changes.
 *
 * The email path ends differently, and the spec is explicit about it (AI Brain §6: "Close at the end
 * of a selection path returns to the start"). A thread is the END of the walk — there is no third
 * level to go on to — and closing it means "I am done with this selection", not "show me that list
 * again": the box returns to the Briefing's own start, where every entry is offered again. Stepping
 * up one level is still available, by clicking the entry crumb itself, so nothing is lost — the ✕
 * is simply the shorter way out that the frame gives it.
 */
export function trailCloseTarget(trail) {
  const segs = (trail || []).filter(Boolean)
  // By POSITION since busy-my-web#606: the chip may stand in the middle of a remembered journey,
  // and the crumbs after it (`ahead`) are not what it steps back to.
  const at = segs.findIndex(s => s.current)
  const current = at > 0 ? segs[at] : null
  const parent = at > 0 ? segs[at - 1] : null
  if (!current || !parent) return null
  // …except a thread opened from the matches list (busy-my-web#608): its way out is that list.
  if (current.kind === 'emails' && current.level === 'thread' && parent.level !== 'matches') return segs[0]
  // The FILE chip takes the ordinary rule, not the thread's (busy-my-web#481, 2026-09-15 parity
  // audit D-4). A thread is the end of a SELECTION path — closing it means "I am done with this
  // selection" — where a viewed file is one of a list the user is still working through: frame
  // 40011255:261043 draws the ✕ on "Contactpage.jpeg" with "24 Attachments" still beside it, and
  // dropping them back at the Briefing's start would cost them the listing they were reading. So
  // the ✕ steps up one level, to the attachments entry, which is the listing itself.
  return parent
}

// ── THE HOME ROOT AND THE +N OVERFLOW (busy-my-web#430 / #427 / #429 / #431, Fatemeh's updated
// breadcrumb, Figma section 40011240-250564) ─────────────────────────────────────────────────────
//
// Fatemeh (Slack, translated): the crumbs are appended until the row has no space left for another;
// then the FIRST item after Home is collapsed into a number, and the number grows as more are
// collapsed. Home is never collapsed and is not counted as the first item — the first item is the
// one after it:
//
//   home ❯ 14 email groups ❯ 3 social media groups ❯ briefing · Linkedin
//   home ❯ +1 ❯ 3 social media groups ❯ briefing · linkedin ❯ 5 Threads
//   home ❯ +2 ❯ briefing · linkedin ❯ 5 Threads ❯ 2 ongoing possible
//
// The sequence is what fixes the cap: three non-Home crumbs stay visible, the rest collapse. It is
// a COUNT rather than a measurement because the crumbs are already capped at 150px each (#352), so
// the widest row three crumbs can make is known — and a rule the tests can state is worth more than
// a ResizeObserver that behaves differently in every column the strip is drawn in.
//
// Pure, like everything else here: what a crumb DOES is still SmartBox's answer.

/** The root the updated frames give every trail. It is never current and never collapses. */
export const HOME_CRUMB = { key: 'home', text: 'Home', icon: 'home', level: 'home' }

/** How many non-Home crumbs stay visible before the leftmost start collapsing. */
export const TRAIL_VISIBLE_MAX = 3

/** `[Home, …items]` — the model the updated breadcrumb renders (busy-my-web#430). */
export function withHomeRoot(trail) {
  const segs = (trail || []).filter(Boolean)
  if (!segs.length) return []
  if (segs[0]?.level === 'home') return segs
  return [{ ...HOME_CRUMB, current: false }, ...segs]
}

/**
 * Collapse the leftmost non-Home crumbs into one `+N` counter until the row fits.
 *
 * @param {Array<object>} trail  the trail, Home first (see `withHomeRoot`)
 * @param {number} max           how many non-Home crumbs stay visible
 * @returns {Array<object>}      Home, then (when anything collapsed) the `+N` crumb carrying what
 *                               it stands for in `collapsed`, then the visible tail.
 */
export function collapseTrail(trail, max = TRAIL_VISIBLE_MAX) {
  const segs = withHomeRoot(trail)
  if (!segs.length) return []
  const [home, ...rest] = segs
  if (rest.length <= max || max < 1) return segs
  // Never the chip (busy-my-web#606): with a journey remembered the chip can sit LEFT of crumbs
  // still to come, and collapsing it would hide where the box is. Only crumbs before it fold.
  const at = rest.findIndex(s => s.current)
  const count = Math.min(rest.length - max, at < 0 ? rest.length - max : at)
  if (count < 1) return segs
  const collapsed = rest.slice(0, count)
  return [
    home,
    {
      key: 'overflow',
      text: `+${collapsed.length}`,
      level: 'overflow',
      overflow: true,
      collapsed,
      current: false,
    },
    ...rest.slice(count),
  ]
}

// ── THE JOURNEY (busy-my-web#606, 2026-10-09 — see the note at the top of this file) ──────────────
//
// A journey is the deepest path walked on a page: the live trail's segments, minus the `current`
// flag, minus the steps no crumb can take you back INTO (below). It is a plain array the briefing
// store holds (`briefing/journey`), so every function here is a value in and a value out.

/**
 * The steps a journey does NOT remember: things that are OPENED over a level rather than walked to
 * — an entity in the box, a file in the viewer, the Ongoing picker and its search. Re-opening them
 * needs more than a crumb knows (the entity's edit payload, the picker's timeline ids), and closing
 * them is a Close, which forgets anyway. Home and the +N counter are presentation, never steps.
 */
const UNREMEMBERED = ['entity', 'attachment', 'picker', 'picker-search', 'home', 'overflow']
const remembered = (seg) => !!seg && !UNREMEMBERED.includes(seg.level)

/**
 * WHICH place a segment is, regardless of how its words read right now.
 *
 * Counts change while you stand somewhere ("2 tasks" becomes "3 tasks") and an open pack reads
 * "<name>, Open" where its remembered crumb reads "<name>", so identity is the level plus the one
 * thing that picks it out: the filter, the class, the record's id. And "1 research pack" is ONE
 * place under two spellings — the story's research filter (`kind: research`) and the pack trail's
 * `packs` level — which is exactly the crumb Saffet clicked: they must match, or climbing out of a
 * pack would look like a new walk and forget the pack.
 */
export function journeySegId(seg) {
  if (!seg) return ''
  const level = String(seg.level || '')
  if (level === 'packs' || (level === 'kind' && seg.filter === 'research')) return 'packs'
  if (['root', 'relations', 'matches', 'commerce-emails'].includes(level)) return level
  const which = seg.ref ?? seg.filter ?? seg.attachmentClass ?? seg.name ?? seg.text
  return `${level}:${which ?? ''}`
}

const isPrefixOf = (head, segs) => head.length <= segs.length
  && head.every((s, i) => journeySegId(s) === journeySegId(segs[i]))

/** A segment as the journey keeps it — no `current`, no `ahead`, nothing about this moment. */
const stored = (seg) => {
  const kept = { ...seg }
  delete kept.current
  delete kept.ahead
  return kept
}

/**
 * The journey after the box moved (the store's `recordJourney`).
 *
 * The live trail is rooted ("Briefing" first) and either still ON the remembered path — a crumb
 * climbed, the page at rest — in which case the journey is kept, with the levels the box stands
 * on refreshed (their counts may have moved); or OFF it — deeper, or somewhere else — in which
 * case it IS the new journey. A trail with no root (nothing walked from the Briefing, #241; the
 * timeline's two-crumb journey, #383) leaves the journey alone: those are arrivals, not walks, and
 * the walk comes back untouched when they close.
 */
export function nextJourney(journey, live) {
  const prev = (journey || []).filter(Boolean)
  // A live crumb with no words of its own (see `mergeJourney`) keeps the ones it was walked with.
  const walked = (live || []).filter(remembered).map(stored)
    .map((s, i) => (s.text || journeySegId(prev[i]) !== journeySegId(s) ? s : { ...s, text: prev[i].text }))
  if (!walked.length || walked[0].level !== 'root') return prev
  if (isPrefixOf(walked, prev)) return prev.map((s, i) => (i < walked.length ? walked[i] : s))
  return walked
}

/**
 * The journey with everything BELOW `seg` forgotten — the ✕ on an open thing, or the gutter's
 * Close (busy-my-web#606: "closing something explicitly forgets the segments below it"). `seg`
 * is the level the close steps back to, and it is kept. Not on the path: nothing is forgotten.
 */
export function forgetJourneyBelow(journey, seg) {
  const prev = (journey || []).filter(Boolean)
  const at = prev.findIndex(s => journeySegId(s) === journeySegId(seg))
  return at < 0 ? prev : prev.slice(0, at + 1)
}

/**
 * The trail to DRAW: the live one, extended by the remembered crumbs deeper than it.
 *
 * Only when the live trail is rooted, is a prefix of the journey, and has nothing OPEN at its tip
 * that a journey does not remember (an entity, a file): the open thing is then the end of the
 * path, and crumbs drawn past it would be places "after" something layered on top of the page. The
 * chip stays where the live trail put it; the deeper crumbs are `ahead` — not current, carrying
 * their remembered words (a pack's bare name rather than "<name>, Open", since it is not open
 * now). A live crumb with no words of its own (the email entry under an open sender group) borrows
 * the journey's.
 */
export function mergeJourney(live, journey) {
  const segs = (live || []).filter(Boolean)
  const prev = (journey || []).filter(Boolean)
  if (!segs.length || segs[0].level !== 'root') return segs
  // Words first, wherever the walk still agrees with the journey — even past where it now goes on.
  const filled = segs.map((s, i) => (s.text || journeySegId(prev[i]) !== journeySegId(s) ? s : { ...s, text: prev[i].text || s.text }))
  if (!filled.every(remembered) || !isPrefixOf(filled, prev)) return filled
  const ahead = prev.slice(segs.length).map(s => ({ ...s, text: s.name || s.text, open: false, current: false, ahead: true }))
  return [...filled, ...ahead]
}

/**
 * Does the briefing on screen still OFFER the level a remembered crumb names (busy-my-web#653)?
 *
 * Only the levels that ARE one of the briefing's rows can be asked: the story's possible
 * connections (`storyRelations`), a kind (`filter`), and the packs list — the story's research row.
 * Those rows are absent at zero ("Absent at zero, like every other row in this briefing",
 * lib/briefings/story.js), so a missing row means the place itself is gone. Anything else the
 * briefing cannot speak for, and is kept.
 */
export function levelStillOffered(seg, briefing) {
  const rows = (briefing?.rows || []).filter(Boolean)
  const level = String(seg?.level || '')
  if (level === 'relations') return rows.some(r => r.storyRelations)
  if (level === 'packs') return rows.some(r => r.filter === 'research')
  if (level === 'kind' && seg.filter) return rows.some(r => r.filter === seg.filter)
  return true
}

/**
 * The drawn trail without the remembered crumbs whose place no longer exists (busy-my-web#653).
 *
 * #606 keeps the deeper crumbs of a journey on the trail after the box climbs back out — but a
 * crumb for a level that has gone (every possible connection answered, the last task of a kind
 * deleted) offers a count that is no longer true and a place a click cannot restore. The FIRST
 * `ahead` crumb sits directly under the chip, so the briefing on screen is the one listing it;
 * when it no longer does, that crumb and everything reached through it go. Drawing only: the
 * stored journey is untouched (`nextJourney` reads the live trail), so a level that comes back
 * brings its crumbs back with it. The chip needs no moving — a gone level is never live, so the
 * live trail already stands on its parent.
 */
export function withoutGoneLevels(trail, briefing) {
  const segs = (trail || []).filter(Boolean)
  const at = segs.findIndex(s => s.ahead)
  if (at < 0 || levelStillOffered(segs[at], briefing)) return segs
  return segs.slice(0, at)
}

/** The crumb one step out of the chip — by position, `ahead` crumbs never count. Null on the root. */
export function trailActiveParent(trail) {
  const segs = (trail || []).filter(Boolean)
  const at = segs.findIndex(s => s.current)
  return at > 0 ? segs[at - 1] : null
}

/**
 * The remembered crumbs a click on `seg` (an `ahead` crumb) walks back down through, in order —
 * every one between the chip and it, then it. SmartBox replays each one's forward step.
 */
export function journeyStepsTo(trail, seg) {
  const segs = (trail || []).filter(Boolean)
  const from = segs.findIndex(s => s.current)
  const to = segs.findIndex(s => s.ahead && journeySegId(s) === journeySegId(seg))
  if (to < 0 || to <= from) return []
  return segs.slice(from + 1, to + 1)
}

/**
 * WHERE THE CHIP'S ✕ GOES, or null for no ✕ at all (busy-my-web#606, Saffet 2026-10-09: "No there
 * should not be a x in front of each breadcrumb").
 *
 * The ✕ is for an OPEN thing — a pack, an entity, a file, a thread, the picker, a matches table
 * (`CLOSABLE` below) — and closes it. A level merely walked to (a kind, an entry, the packs list) has no ✕:
 * its way out is the crumb before it, or the gutter's Back. Where it goes is `trailCloseTarget`.
 */
// …and the two possible-matches TABLES (the mailbox's and a story's), which are opened over the
// middle and whose ✕ has always been how they close.
const CLOSABLE = ['pack', 'entity', 'attachment', 'thread', 'picker', 'picker-search', 'matches', 'relations']
export function trailChipClose(trail) {
  const segs = (trail || []).filter(Boolean)
  const current = segs.find(s => s.current) || null
  if (!current || !(current.open || CLOSABLE.includes(current.level))) return null
  return trailCloseTarget(segs)
}

export default briefingTrail
